import _ from 'lodash'
import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import {
	EditorFeatureConfigSymbol,
	PlatformResetCompsSymbol,
	PlatformSymbol,
	CurrentRouteInfoSymbol,
	LivePreviewEnvDataSetterSymbol,
	ControllersInvokerSymbol,
	ControllersInvoker,
	ExperimentsSymbol,
	Experiments,
	PlatformDsApi,
} from '@wix/thunderbolt-symbols'
import { createPromise } from '@wix/thunderbolt-commons'
import type { IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import type { PlatformResetComps } from 'thunderbolt-platform'
import { IViewerBatchingManager, ViewerBatchingManagerSymbol } from 'ds-feature-model-updates-invoker'
import { CandidateRouteInfo, ICurrentRouteInfo, IRoutingMiddleware, RoutingMiddleware } from 'feature-router'
import type { RefreshAppsOptions, ViewerManagerLivePreviewApiEditorConfig } from './types'
import { name } from './symbols'
import { HOOKS_BLACK_LIST, SOURCES, DEBOUNCE_REFRESH_APPS_TIME } from './constants'
import { aggregateRefreshParams } from './livePreviewUtils'
import { LivePreviewEnvDataSetter } from 'thunderbolt-platform'

const shouldTriggerPlatformByBlacklist = (hookName?: string) => !hookName || !HOOKS_BLACK_LIST.includes(hookName)

const normalizeControllerId = (controllerId: string) => controllerId.replace('-dataQuery', '')

const livePreviewRequestsOptions: Array<RefreshAppsOptions> = []
export const ViewerManagerLivePreviewApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		PlatformSymbol,
		PlatformResetCompsSymbol,
		LivePreviewEnvDataSetterSymbol,
		ViewerBatchingManagerSymbol,
		CurrentRouteInfoSymbol,
		ControllersInvokerSymbol,
		ExperimentsSymbol,
		optional(RoutingMiddleware.Dynamic),
	],
	(
		featureConfig: ViewerManagerLivePreviewApiEditorConfig,
		platformApi: PlatformDsApi,
		resetCompsManager: PlatformResetComps,
		livePreviewOptionsDataSetter: LivePreviewEnvDataSetter,
		viewerBatchingManager: IViewerBatchingManager,
		currentRouteInfo: ICurrentRouteInfo,
		controllersInvoker: ControllersInvoker,
		experiments: Experiments,
		dynamicPagesRoutingMiddleware?: IRoutingMiddleware
	): IViewerManagerImplementor<'livePreview'> => {
		const refetchDynamicPageData = async () => {
			const routeInfo = currentRouteInfo.getCurrentRouteInfo()

			if (routeInfo?.dynamicRouteData && dynamicPagesRoutingMiddleware) {
				// Call the handler to get the new dynamicRouteData after update
				const dynamicRouteInfo = await dynamicPagesRoutingMiddleware.handle({
					...routeInfo,
					// This needs to be undefined in order for the handler to trigger again
					pageId: undefined,
				} as Partial<CandidateRouteInfo>)
				if (dynamicRouteInfo?.dynamicRouteData) {
					// Update it on currentRouteInfo symbol
					currentRouteInfo.updateCurrentDynamicRouteInfo(dynamicRouteInfo.dynamicRouteData)
				}
			}
		}

		const isUpdateConfigSupported = (options: RefreshAppsOptions) =>
			options.controllersToRefresh!.every((controllerId) =>
				controllersInvoker.isRegisteredToConfigUpdate(normalizeControllerId(controllerId))
			)

		const shouldTriggerPlatform = (options: RefreshAppsOptions) => {
			if (options.source === SOURCES.AFTER_PROP_UPDATE) {
				return !isUpdateConfigSupported(options)
			}
			return shouldTriggerPlatformByBlacklist(options.source)
		}

		const executeRefreshRequest = async () => {
			const { shouldFetchData, compsIdsToReset, apps, controllersToRefresh } = aggregateRefreshParams(
				livePreviewRequestsOptions
			)
			livePreviewOptionsDataSetter.set({ shouldFetchData, compsIdsToReset, apps, controllersToRefresh })

			if (shouldFetchData) {
				await refetchDynamicPageData()
			}

			if (
				controllersToRefresh?.length &&
				experiments['specs.thunderbolt.support_controllersToRefresh_livePreview_flag']
			) {
				const { promise: renderBlockingPromise, resolver: unblockRender } = createPromise()
				viewerBatchingManager.addRenderBlockingTask(renderBlockingPromise)
				if (compsIdsToReset) {
					resetCompsManager.resetComps(compsIdsToReset)
				}
				Promise.all(
					controllersToRefresh.map((controllerId) =>
						controllersInvoker.invokePageReady(normalizeControllerId(controllerId))
					)
				).finally(() => {
					unblockRender()
					livePreviewOptionsDataSetter.set({})
				})
			} else {
				platformApi
					.runPlatform({ deferPlatformUpdates: true })
					.then(({ flushPlatformUpdates }) => {
						const { promise: renderBlockingPromise, resolver: unblockRender } = createPromise()
						viewerBatchingManager.addRenderBlockingTask(renderBlockingPromise)
						if (compsIdsToReset) {
							resetCompsManager.resetComps(compsIdsToReset)
						}
						flushPlatformUpdates()
						unblockRender()
					})
					.finally(() => {
						// clean live preview options after each platform execution
						livePreviewOptionsDataSetter.set({})
					})
			}
			livePreviewRequestsOptions.length = 0
		}

		const debounceRefreshRequest = _.debounce(executeRefreshRequest, DEBOUNCE_REFRESH_APPS_TIME)

		return {
			// Live preview api should be invoked when nothing changes in the document.
			// for example, when data-binding collections are altered.
			// In this case, we'll re-run the apps/controllers lifecycle methods.
			// In the case that the document has changed, we have a dedicated controllers-hooks (updateConfig)
			livePreview: {
				refreshAppsInCurrentPage(options) {
					if (shouldTriggerPlatform(options)) {
						livePreviewRequestsOptions.push(options as RefreshAppsOptions)
						if (options.immediate) {
							executeRefreshRequest()
						} else {
							debounceRefreshRequest()
						}
					}
				},
				getAllowedApps() {
					return featureConfig.livePreviewApps
				},
				isLivePreviewOpen() {
					return featureConfig.isLivePreviewEnabled
				},
				triggerOnAppSettingsUpdate({ compId: controllerId, updates }) {
					controllersInvoker.invokeAppSettingsUpdate(controllerId, updates)
				},
				triggerWidgetStateUpdate({ compId, widgetViewState }) {
					controllersInvoker.invokeWidgetViewStateUpdate(compId, widgetViewState)
				},
			},
		}
	}
)
